import { createFetcher } from '@finn/ui-utils';
import { useFormContext } from 'react-hook-form';
import useSWR from 'swr';

import { Primitive } from '../../../types';
import { ChangeFee, UpdateReturnData } from '../../../types/bff-api';

export const constructReturnRequest = (
  formData: Record<string, Primitive>
): UpdateReturnData | null => {
  const returnType = formData.return_type;

  if (returnType === 'SELF_RETURN') {
    return {
      return_type: returnType,
      preferred_return_date: formData.slot as string,
    };
  } else {
    const candidateData = {
      return_firstname: formData.firstname as string,
      return_lastname: formData.lastname as string,
      return_street: formData.street as string,
      return_housenumber: formData.housenumber as string,
      return_city: formData.city as string,
      return_zip: formData.postcode as string,
      return_address_extra: formData.address_suffix as string,
      return_type: returnType as UpdateReturnData['return_type'],
      return_phone_number: formData.phone_number as string,
      return_email: formData?.email as string,
      preferred_return_date: formData.slot as string,
    };

    const nonEmptyFields = [
      'return_firstname',
      'return_lastname',
      'return_housenumber',
      'return_street',
      'return_city',
      'return_zip',
      'return_phone_number',
      'preferred_return_date',
    ];

    // if some mandatory fields are empty, return null
    if (
      Object.entries(candidateData).some(
        ([key, value]) => nonEmptyFields?.includes(key) && !value
      )
    ) {
      return null;
    }

    return candidateData;
  }
};

const generateKey = (returnData: Record<string, Primitive | undefined>) => {
  return `${returnData.return_street}-${returnData.return_housenumber}-${returnData.return_city}-${returnData.return_zip}-${returnData.preferred_return_date}`;
};

export const useReturnChangeFee = (dealId: string | undefined | null) => {
  // this hook always is as part of form
  const formMethods = useFormContext();
  const returnData = constructReturnRequest(formMethods?.getValues?.() || {});
  const isAnyInputFocused =
    typeof window === 'object' && document.activeElement?.tagName === 'INPUT';
  const shouldFetch =
    dealId &&
    returnData &&
    Boolean(formMethods?.getValues?.('return_type')) &&
    !isAnyInputFocused &&
    formMethods?.formState.isValid;
  const normalizedReturnData =
    returnData?.return_type === 'PICKUP'
      ? {
          ...returnData,
          // as this field is optional, it arrives as undefined but the text input makes it an empty string
          // if empty, make it undefined again to achieve caching consistency
          return_address_extra: returnData.return_address_extra || undefined,
        }
      : {
          return_type: returnData?.return_type,
          preferred_return_date: returnData?.preferred_return_date,
        };

  const { data: responseData, isLoading } = useSWR<{
    data: ChangeFee;
  }>(
    shouldFetch
      ? `${dealId}/return-change-fee:${generateKey(normalizedReturnData)}`
      : null,
    shouldFetch && normalizedReturnData
      ? () =>
          createFetcher({
            baseURL: '',
            withCredentials: true,
          })({
            url: `/portal/api/bff/deals/${dealId}/return-change-fee`,
            method: 'POST',
            body: normalizedReturnData,
          }) as Promise<{
            data: ChangeFee;
          }>
      : null
  );

  return {
    data: responseData?.data?.amount,
    isLoading,
  };
};
